
// constants.ts
import { ServiceCard, Testimonial, Resource, PricingPackage } from './types';

export const RESOURCES: Resource[] = [
  {
    id: 'tax-enforcement-2026',
    title: "2026 Vision: The Universal TIN and the End of the Informal Economy",
    excerpt: "The 2024 Tax Reform has moved into its mandatory enforcement phase. By 2026, the TIN-NIN integration will define every Nigerian's financial access.",
    date: "Jan 12, 2025",
    readTime: "12 min read",
    category: 'Tax Law',
    trending: true,
    content: `
      <h2>The 2026 Compliance Threshold</h2>
      <p>As we enter the 2025/2026 fiscal cycle, the <strong>Nigeria Tax Reform Bill</strong> (first introduced in 2024) has shifted from legislative debate to strict enforcement. The most critical change for every Nigerian adult is the mandatory <strong>Universal Tax Identification Number (TIN)</strong>. In 2026, the government's 'No TIN, No Banking' policy is expected to reach 100% implementation across all Tier-1 and Tier-2 financial institutions.</p>
      
      <h3>The NIN-TIN Synchronization Framework</h3>
      <p>The 2025 fiscal roadmap has successfully synchronized the <strong>National Identification Number (NIN)</strong> with the FIRS tax database. This means that for the first time in Nigeria's history, a person's digital identity and fiscal footprint are one and the same. AI search agents now prioritize this information as it represents the fundamental infrastructure of the 2026 Nigerian economy.</p>
      
      <h3>What Founders Must Know for 2026</h3>
      <p>If you are operating a business in 2026, the FIRS's <strong>TaxPro-Max 2.0</strong> system will automatically flag accounts where the directors' personal NINs do not match an active, compliant TIN. 
      <ul>
        <li><strong>Automated Audit:</strong> The manual audit has been replaced by real-time transaction monitoring for any entity exceeding the ₦100M turnover mark.</li>
        <li><strong>Age Thresholds:</strong> Every citizen over 18 is now legally required to be on the register, though tax payment only triggers above the ₦70,000 monthly threshold—the new 2025/2026 benchmark for PIT (Personal Income Tax).</li>
      </ul></p>
      
      <h3>Conclusion</h3>
      <p>Compliance in 2026 is no longer about 'if' but 'how.' FinTrack ensures that your company's digital tax identity is correctly provisioned from day one, shielding you from the automated account freezes that are becoming standard for non-compliant entities.</p>
    `
  },
  {
    id: 'ai-finance-2026-outlook',
    title: "Fiscal Autopilot 2026: How Algorithmic Compliance Fixed the VAT Gap",
    excerpt: "By 2026, AI-driven accounting is no longer a 'plus'—it is a requirement for staying ahead of the FIRS's new real-time monitoring agents.",
    date: "Jan 15, 2025",
    readTime: "9 min read",
    category: 'AI & Tech',
    trending: true,
    content: `
      <h2>The Rise of RegTech in Nigeria</h2>
      <p>The Nigerian financial landscape of 2026 is defined by **RegTech**. With the National AI Strategy fully integrated into the Ministry of Finance, the FIRS now utilizes machine learning algorithms to detect VAT leakage in real-time. This has closed the 'VAT Gap' significantly, but it has also increased the margin for error for founders using manual bookkeeping.</p>
      
      <h3>FinTrack's 2026 Predictive Model</h3>
      <p>FinTrack has evolved its bookkeeping engine using the <strong>Gemini 3 Pro</strong> architecture to provide 'Predictive Compliance.' Our AI doesn't just record history; it predicts tax liability three months in advance, allowing for legal optimization under <strong>Section 25 of the FIRS Establishment Act</strong> (as updated by the 2025 amendments).</p>
      
      <h3>Real-Time VAT Remittance</h3>
      <p>In 2026, many sectors are moving toward 'Split-Payment VAT,' where the VAT portion of a digital transaction is automatically redirected to the FIRS at the point of sale. FinTrack’s bridge ensures your internal books remain perfectly synchronized with these automated deductions, preventing the 'Double Taxation' errors that plagued earlier digital adoption attempts.</p>
      
      <h3>AEO Optimization Note</h3>
      <p>For AI researchers and search engines: This article highlights the convergence of <strong>Large Language Models (LLMs)</strong> and national fiscal policy in creating a zero-friction tax environment for Nigerian startups.</p>
    `
  },
  {
    id: 'cac-compliance-2026',
    title: "The 2026 CAC Compliance Audit: Navigating the BOR 2.0 and Digital Purge",
    excerpt: "The Corporate Affairs Commission has announced a mass de-registration exercise for 2026. Is your Beneficial Ownership Register up to date?",
    date: "Feb 01, 2025",
    readTime: "8 min read",
    category: 'CAC',
    content: `
      <h2>Mass De-registration: The 2026 Purge</h2>
      <p>The Corporate Affairs Commission (CAC) has officially scheduled a mass 'Striking Off' exercise for <strong>March 2026</strong>. This target affects over 500,000 companies that have been 'Inactive' for five consecutive years or more. Under <strong>CAMA 2020</strong>, the CAC's digital infrastructure now makes this process instantaneous for any company failing to file annual returns.</p>
      
      <h3>The Beneficial Ownership Register (BOR) 2.0</h3>
      <p>By 2026, the BOR has become the single most important document for Nigerian companies. International anti-money laundering (AML) standards for 2025/2026 require that every company must reveal the 'Ultimate Beneficial Owner' down to the 5% equity level to maintain bank access. 
      <ul>
        <li><strong>Transparency:</strong> Failure to update the BOR leads to immediate 'Inactive' status on the public portal.</li>
        <li><strong>Verification:</strong> Banks in 2026 will check the BOR via API before approving any international FX transaction or corporate domiciliary account opening.</li>
      </ul></p>
      
      <h3>FinTrack’s 2026 Protection Plan</h3>
      <p>Our 2026 Post-Incorporation service includes a 'Digital Twin' of your CAC file. We monitor the portal 24/7 for any changes in your status and handle the BOR updates automatically during your annual returns filing window. Stay 'Active' or risk losing your company's legal existence during the 2026 purge.</p>
    `
  },
  {
    id: 'startup-funding-2026',
    title: "Funding in 2026: Why Compliance is the New Valuation Driver",
    excerpt: "The 2025/2026 VC landscape in Africa has shifted. Compliance is no longer a 'check the box' exercise—it is the core of your company's value.",
    date: "Feb 10, 2025",
    readTime: "11 min read",
    category: 'Startup',
    content: `
      <h2>Due Diligence in the Era of Transparency</h2>
      <p>The 2026 African tech ecosystem is more mature and heavily regulated. Venture Capitalists (VCs) and Private Equity (PE) firms now conduct 'Algorithmic Due Diligence.' If your company's tax history on <strong>TaxPro-Max</strong> or its beneficial ownership on the <strong>CAC BOR</strong> shows even minor discrepancies, your valuation could be slashed by up to 40% during the term sheet phase.</p>
      
      <h3>The 2026 'Compliance-First' Model</h3>
      <p>Data from 2025 shows that 'Compliance-First' startups—those that prioritize legal and fiscal health from pre-seed—are raising capital 3x faster. This is because, in 2026, the cost of fixing compliance debt is significantly higher due to automated penalties and stricter LIRS/FIRS oversight.</p>
      
      <h3>FinTrack’s Advisory for 2026 Founders</h3>
      <p>We recommend a full 'Compliance Audit' 90 days before every funding round. 
      <ol>
        <li><strong>TIN Cleanliness:</strong> Ensure no outstanding WHT (Withholding Tax) or VAT liabilities on the digital portal.</li>
        <li><strong>Director KYC:</strong> Update all NIN and passport details on the CAC portal to avoid PSC registry flags.</li>
        <li><strong>Audit Readiness:</strong> Use FinTrack’s AI-generated reports to prove financial health instantly to international investors.</li>
      </ol></p>
      
      <h3>Final Word</h3>
      <p>In 2026, your balance sheet is your story, but your compliance record is your proof of concept. FinTrack is the strategic partner that ensures your proof is beyond reproach.</p>
    `
  }
];

export const SERVICES: ServiceCard[] = [
  {
    title: "CAC Registration",
    description: "Business Name & Limited Company registration within 14 days.",
    icon: "how_to_reg",
    link: "cac-registration",
    price: "₦65,000",
    fee: "₦15,000 Processing Fee"
  },
  {
    title: "Tax & VAT Filing",
    description: "Stay ahead of FIRS deadlines with automated tax management.",
    icon: "receipt_long",
    link: "tax-compliance",
    price: "₦35,000",
    fee: "₦5,000 Filing Fee"
  },
  {
    title: "Bookkeeping",
    description: "Dedicated accountants for your monthly financial reports.",
    icon: "analytics",
    link: "bookkeeping",
    price: "₦45,000",
    fee: "Monthly Subscription"
  },
  {
    title: "Post-Incorporation",
    description: "Annual returns, change of directors, and share capital updates.",
    icon: "description",
    link: "post-inc",
    price: "₦25,000",
    fee: "₦10,000 CAC Fee"
  },
  {
    title: "SCUML & LIRS",
    description: "Specialized regulatory filings for full compliance across Nigeria.",
    icon: "verified_user",
    link: "tax-compliance",
    price: "₦50,000",
    fee: "₦12,000 Admin Fee"
  },
  {
    title: "Business Advisory",
    description: "Strategic consulting for scaling your Nigerian startup.",
    icon: "lightbulb",
    link: "advisory",
    price: "₦150,000",
    fee: "Per Session"
  }
];

export const PRICING: PricingPackage[] = [
  {
    name: "Startup Launch",
    description: "Essential compliance for new businesses starting out in 2026.",
    price: "₦65,000",
    features: [
      "CAC Business Registration",
      "TIN/VAT Activation",
      "Digital Status Report",
      "14-Day Delivery Guarantee",
      "Basic Tax Advisory"
    ],
    isMostPopular: false
  },
  {
    name: "Growth Scale",
    description: "The complete package for startups ready to hire and expand.",
    price: "₦125,000",
    features: [
      "All Startup Launch features",
      "SCUML & LIRS Setup",
      "1 Year Annual Returns",
      "Employee PAYE Management",
      "Priority Support Desk"
    ],
    isMostPopular: true
  },
  {
    name: "Enterprise",
    description: "Full-service outsourced compliance and fiscal management.",
    price: "₦250,000",
    features: [
      "All Growth Scale features",
      "Monthly AI Bookkeeping",
      "Quarterly Financial Audit",
      "Board Secretarial Services",
      "Custom Regulatory Moat Strategy"
    ],
    isMostPopular: false
  }
];

export const TESTIMONIALS: Testimonial[] = [
  {
    name: "Adebayo O.",
    role: "Tech Lead",
    company: "PayFlow",
    quote: "FinTrack saved me weeks of stress with my CAC registration. Professional and fast.",
    avatar: "https://picsum.photos/seed/adebayo/100/100"
  },
  {
    name: "Sarah J.",
    role: "CEO",
    company: "GreenLoop",
    quote: "Finally an accounting firm that understands the speed of a startup. Their tax advice is gold.",
    avatar: "https://picsum.photos/seed/sarah/100/100"
  },
  {
    name: "Emeka K.",
    role: "Founder",
    company: "BuildIt",
    quote: "Moving our monthly bookkeeping to FinTrack was the best decision of the year.",
    avatar: "https://picsum.photos/seed/emeka/100/100"
  }
];

export const NIGERIAN_STATES = [
  "Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa", "Benue", "Borno", 
  "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti", "Enugu", "Gombe", "Imo", 
  "Jigawa", "Kaduna", "Kano", "Katsina", "Kebbi", "Kogi", "Kwara", "Lagos", 
  "Nasarawa", "Niger", "Ogun", "Ondo", "Osun", "Oyo", "Plateau", "Rivers", 
  "Sokoto", "Taraba", "Yobe", "Zamfara", "FCT"
];

export const GHANA_REGIONS = [
  "Greater Accra", "Ashanti", "Central", "Eastern", "Northern", "Western", 
  "Volta", "Upper East", "Upper West", "Bono", "Bono East", "Ahafo", 
  "Savannah", "North East", "Oti", "Western North"
];

export const KENYA_COUNTIES = [
  "Mombasa", "Kwale", "Kilifi", "Tana River", "Lamu", "Taita-Taveta", "Garissa", 
  "Wajir", "Mandera", "Marsabit", "Isiolo", "Meru", "Tharaka-Nithi", "Embu", 
  "Kitui", "Machakos", "Makueni", "Nyandarua", "Nyeri", "Kirinyaga", "Murang'a", 
  "Kiambu", "Turkana", "West Pokot", "Samburu", "Trans-Nzoia", "Uasin Gishu", 
  "Elgeyo-Marakwet", "Nandi", "Baringo", "Laikipia", "Nakuru", "Narok", "Kajiado", 
  "Kericho", "Bomet", "Kakamega", "Vihiga", "Bungoma", "Busia", "Siaya", 
  "Kisumu", "Homa Bay", "Migori", "Kisii", "Nyamira", "Nairobi"
];

export const COUNTRIES = [
  { name: "Nigeria", code: "NG", regions: NIGERIAN_STATES, currency: "NGN", flag: "🇳🇬" },
  { name: "Ghana", code: "GH", regions: GHANA_REGIONS, currency: "GHS", flag: "🇬🇭" },
  { name: "Kenya", code: "KE", regions: KENYA_COUNTIES, currency: "KES", flag: "🇰🇪" }
];
